// Run-Prototyp im Browser (Phase 4, §21): Titel, Fluss, Ufer, Chronik, Run-Übersicht.
// Hält den Run-Zustand, speichert ihn nach jeder Änderung im Browser (übersteht Neuladen) und verteilt die Eingabe:
// Auf dem Ufer steuert der Zeiger, in Menüs springt der Fokus von Knopf zu Knopf (X10).
import { biomeOfDay, gameMeta, getItem, getSpecies, itemLabel, itemText, runRules, systemOn } from '../content/index.ts';
import {
  autoPlace,
  battleSetup,
  combatProfile,
  daylight,
  displayName,
  ensureLooks,
  fieldSlotsOf,
  friendById,
  levelForXp,
  newRun,
  resolveFight,
  retreatOptions,
  runBattle,
  type BattleSetup,
  type EnemyPlacement,
  type RunState,
  xpForLevel,
  xpToNext,
} from '../sim/index.ts';
import * as sim from '../sim/index.ts';
import { createInput, type Action } from './input.ts';
import { createPulse, focusFirst, focusables, moveFocus } from './nav.ts';
import {
  bindDialog,
  chronicleHtml,
  dialogBack,
  dialogHtml,
  endHtml,
  renderRiver,
  resetLater,
  riverAction,
  startNaming,
  openFriendCard,
  type RunApp,
  pathSteps,
} from './run-views.ts';
import { initTips } from './tips.ts';
import { bindFahrtCanvas, fahrtDebug, fahrtFrame, fastForward, initFahrt, insideAudible, traveling } from './fahrt/index.ts';
import { applyCardZoom, paintKarten } from './karte.ts';
import { addToLibrary, loadLibrary, recordHtml, setLibraryLine, shelfHtml } from './library.ts';
import { drawArrival, loadEnde, regalHtml } from './ende.ts';
import { presentScene, sceneCanvas } from './pixel.ts';
import { sprite } from './art.ts';
import { initSound, play, setSoundScene, soundDebug, soundFrame } from './sound.ts';
import { closeMenu, initMenu, menuKey, menuMove, menuOpen, toggleMenu } from './menu.ts';
import { settings } from './settings.ts';
import { applyTwig } from './twig.ts';
import { drawMood } from './mood.ts';
import { initPractice, openUebungsufer, practiceAction, practiceDebug, practiceEsc, practiceFrame } from './practice/index.ts';
import { initShore, openPractice, openRunFight, replayRunFight, shoreAction, shoreDebug, shoreFrame, shoreRedraw, type Member } from './shore.ts';

type View = 'title' | 'river' | 'shore' | 'chronicle' | 'end' | 'practice';
const SAVE_KEY = 'fff.run.v1';
/** Der Kampf, der gerade abgespielt wird: übersteht das Neuladen (Fabi, Spieltest Phase 4) */
const FIGHT_KEY = 'fff.fight.v1';
const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

const app = {
  run: null as RunState | null,
  view: 'title' as View,
  /** Auf dem Ufer läuft gerade ein Kampf des Runs; erst „Weiter“ führt zurück zum Fluss */
  inShore: false,
  retreat: 'normal',
};

// ---------- Speicherstand ----------

function save(): void {
  try {
    if (app.run) localStorage.setItem(SAVE_KEY, JSON.stringify(app.run));
    else localStorage.removeItem(SAVE_KEY);
  } catch {
    // Ohne Speicher (privates Fenster) läuft das Spiel trotzdem, nur ohne Fortsetzen
  }
}

function load(): RunState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const run = JSON.parse(raw) as RunState;
    if (run.version !== 1) return null;
    // T37: ältere Spielstände kennen das Aussehen noch nicht
    ensureLooks(run);
    return run;
  } catch {
    return null;
  }
}

/**
 * Ein Kampf ist ausgerechnet, sobald man „Los“ drückt; der Run ist dann schon weiter. Damit Neuladen ihn nicht
 * überspringt, merken wir uns Aufstellung und Startwert und spielen ihn nach dem Laden noch einmal ab.
 */
type SavedFight = {
  runSeed: number;
  /** Länge der Chronik direkt nach dem Kampf: so erkennen wir, dass der Run seitdem nicht weiter ist */
  chronLen: number;
  title: string;
  roster: Member[];
  fieldSlots: number;
  enemies: EnemyPlacement[];
  storm: string;
  retreat: string;
  setup: BattleSetup;
  seed: number;
  notes: string;
  kept?: string[];
  /** R41, R42 (Erste Fassung): Biom und ob es der Durchlass ist, für das Bild beim Wiederholen */
  biome?: string;
  boss?: boolean;
};

function saveFight(f: SavedFight | null): void {
  try {
    if (f) localStorage.setItem(FIGHT_KEY, JSON.stringify(f));
    else localStorage.removeItem(FIGHT_KEY);
  } catch {
    // ohne Speicher eben ohne Wiederholung
  }
}

function loadFight(run: RunState): SavedFight | null {
  try {
    const raw = localStorage.getItem(FIGHT_KEY);
    if (!raw) return null;
    const f = JSON.parse(raw) as SavedFight;
    return f.runSeed === run.seed && f.chronLen === run.chronicle.length ? f : null;
  } catch {
    return null;
  }
}

// ---------- Bildschirme ----------

function setView(v: View): void {
  app.view = v;
  document.body.dataset.view = v;
  if (v !== 'river') closeDialog();
}

function closeDialog(): void {
  document.body.classList.remove('dialog-open');
}

const runApp: RunApp = {
  get run() {
    return app.run!;
  },
  sync: () => sync(),
  toTitle: () => showTitle(),
  showChronicle: () => showChronicle(),
};

/** Nach jeder Änderung: speichern, dann zeigen, was der Run gerade braucht */
function sync(): void {
  save();
  const run = app.run;
  if (!run) return showTitle();
  if (app.inShore) return;
  if (run.pending.kind === 'end') return showEnd();
  if (run.pending.kind === 'fight') return openFight();
  showRiver();
}

/** Welches Element hatte den Fokus? Nach dem Neuzeichnen soll es ihn wiederbekommen (Controller) */
function focusKey(): string | null {
  const el = document.activeElement as HTMLElement | null;
  if (!el || el === document.body) return null;
  if (el.id) return `#${el.id}`;
  for (const attr of ['friend', 'pick', 'look', 'item', 'opt', 'actor', 'welcome', 'peek', 'buy', 'send', 'release', 'artifact', 'level', 'equip', 'use', 'give', 'act', 'name']) {
    const v = el.dataset[attr];
    if (v !== undefined) return `[data-${attr}="${CSS.escape(v)}"]`;
  }
  return null;
}

let lastDialog = '';
function showRiver(): void {
  const key = focusKey();
  setView('river');
  renderRiver(runApp);
  const html = dialogHtml(runApp);
  if (html) {
    // T36 (Fabi, Phase Kern, 17.10.2026 nachts): Ein neues Fenster entfaltet sich: erst die Überschrift, nach einem
    // Augenblick der Text, dann die Möglichkeiten. Im Menü lässt es sich ausschalten
    const title = (h: string) => /<h2[^>]*>([\s\S]*?)<\/h2>/.exec(h)?.[1] ?? '';
    const newWindow = !document.body.classList.contains('dialog-open') || title(html) !== title(lastDialog);
    document.body.classList.add('dialog-open');
    const card = $('dialog-card');
    card.classList.toggle('unfold', newWindow && settings().unfold);
    const fresh = html !== lastDialog;
    // Fabi (Phase Kern, 17.10.2026): Das Holz gehört zum Run, nicht zu einer Begegnung oder einem Tier; es steht im
    // Inventar (mit Rahmen), das neben dem Fenster zu sehen ist, nicht mehr oben rechts im Fenster
    card.innerHTML = html;
    bindDialog(runApp);
    const again = key ? (card.querySelector(key) as HTMLElement | null) : null;
    if (again && !fresh) again.focus();
    else if (fresh) {
      // Spieltest Phase 4: Nur wo Benennen der Zweck ist, springt der Fokus ins Namensfeld; nicht beim Ansehen eines Freundes
      const input = card.querySelector('input.name-input:not(#friend-name)') as HTMLInputElement | null;
      if (input && !document.body.classList.contains('pad')) {
        input.focus();
        input.select();
      } else focusFirst(card);
    } else if (again) again.focus();
    else focusFirst(card);
    lastDialog = html;
  } else {
    closeDialog();
    lastDialog = '';
    const again = key ? (document.querySelector(`#view-river ${key}`) as HTMLElement | null) : null;
    if (again) again.focus();
    else if (document.body.classList.contains('pad')) focusFirst($('landing-panel'));
  }
}

function showTitle(): void {
  setView('title');
  const saved = app.run ?? load();
  const running = saved && saved.status === 'running';
  const buttons: string[] = [];
  if (running) buttons.push(`<button class="primary" id="t-continue">Weiterspielen <small>Tag ${saved.day}, ${saved.crew.length} Freunde an Bord</small></button>`);
  // Runde 7: Neuer Run als Kachel mit der Flusskarte des Grafik-Leads (kachel-start), im Holzrahmen; der Text unten links
  const tile = sprite('ui', 'kachel-start');
  buttons.push(
    tile
      ? `<button id="t-new" class="title-tile${running ? '' : ' primary-tile'}"><img src="art/${tile.file}" alt="" aria-hidden="true"><span data-twig="${window.innerWidth < 500 ? 'klein' : 'mittel'}-birke">Neuer Run</span><small>${running ? 'Der gespeicherte Run geht dabei verloren.' : 'Mit dem Floß bis ans Meer.'}</small></button>`
      : `<button ${running ? '' : 'class="primary"'} id="t-new">Neuer Run${running ? ' (der gespeicherte geht verloren)' : ''}</button>`,
  );
  if (saved && !running) buttons.push('<button id="t-last">Letzte Reise ansehen</button>');
  // K46: Das Übungsufer, ein fester Kampf mit Pause und Befehlen; K53 (Fabi, 29.10.2026): ausgeblendet, das Startbild bleibt
  if (systemOn('uebungsufer')) buttons.push('<button id="t-uebungsufer" class="title-uebungsufer"><canvas class="mood" aria-hidden="true"></canvas><span data-twig="mittel-birke">Übungsufer</span><small>Kampf mit Pause und Befehlen</small></button>');
  // M9: Die Bibliothek der Reisen, sobald es eine gibt
  if (loadLibrary().length) buttons.push('<button id="t-library">Bibliothek</button>');
  buttons.push('<button id="t-practice">Kampf-Übung</button>');
  $('title-actions').innerHTML = buttons.join('');
  $('title-note').textContent = 'Drei Tage flussabwärts, vor dem Unwetter her.';
  const on = (id: string, f: () => void) => {
    const el = document.getElementById(id);
    if (el) el.onclick = f;
  };
  on('t-continue', () => {
    app.run = saved;
    resetLater();
    sync();
  });
  on('t-new', () => startNewRun());
  on('t-library', () => showLibrary());
  on('t-last', () => {
    app.run = saved;
    showEnd();
  });
  on('t-practice', () => {
    app.inShore = false;
    setView('shore');
    openPractice();
  });
  on('t-uebungsufer', () => showUebungsufer());
  applyTwig($('title-actions'));
  focusFirst($('title-actions'));
}

/** K46: das Übungsufer öffnen */
function showUebungsufer(): void {
  closeMenu();
  app.inShore = false;
  setView('practice');
  openUebungsufer();
}

/** Ein neuer Run, von der Startseite oder aus dem Menü */
function startNewRun(): void {
  closeMenu();
  saveFight(null);
  app.inShore = false;
  app.run = newRun((Math.random() * 2 ** 31) | 0);
  resetLater();
  startNaming();
  sync();
}

function showChronicle(): void {
  if (!app.run) return;
  setView('chronicle');
  $('chronicle').innerHTML = chronicleHtml(app.run);
  $('chron-back').onclick = () => showRiver();
  focusFirst($('chronicle'));
}

function showEnd(): void {
  if (!app.run) return;
  setView('end');
  // M9: Der Run kommt in die Bibliothek; den Satz fürs Logbuch darf man ändern
  const run = app.run;
  const library = addToLibrary(sim.runRecord(run));
  $('end').innerHTML = endHtml(run, library);
  arrivalStart = performance.now();
  const line = document.getElementById('end-line') as HTMLTextAreaElement | null;
  if (line) line.oninput = () => setLibraryLine(String(run.seed), line.value);
  bindRegal(String(run.seed));
  $('end-new').onclick = () => {
    app.run = newRun((Math.random() * 2 ** 31) | 0);
    resetLater();
    startNaming();
    sync();
  };
  $('end-title').onclick = () => showTitle();
  // M9: Die Seite beginnt oben, bei den drei Fakten; der Fokus liegt trotzdem auf „Neuer Run“
  ($('end-new') as HTMLElement).focus({ preventScroll: true });
  scrollTop();
}

/** Seiten wie das Ende beginnen oben */
function scrollTop(): void {
  window.scrollTo(0, 0);
  for (const el of [document.scrollingElement, $('view-end'), $('end')]) if (el) el.scrollTop = 0;
}

/**
 * Runde 7: Das Regal der Bibliothek. Ein Klick auf ein Buch zeigt die Reise darunter; mit zwei Knöpfen blättert man
 * zwischen den Regalen, wenn es mehr Reisen gibt, als in eines passen
 */
function bindRegal(current?: string): void {
  const root = document.getElementById('end-regal');
  const detail = document.getElementById('book-detail');
  if (!root) return;
  const list = loadLibrary();
  root.onclick = (ev) => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>('[data-book], [data-regal]');
    if (!el) return;
    if (el.dataset.regal !== undefined) {
      root.innerHTML = regalHtml(list, current, Number(el.dataset.regal));
      return;
    }
    const i = list.findIndex((r) => r.id === el.dataset.book);
    if (i >= 0 && detail) detail.innerHTML = recordHtml(list[i]!, current, i + 1);
    for (const b of root.querySelectorAll('.book.picked')) b.classList.remove('picked');
    el.classList.add('picked');
  };
}

/** M9: Die Bibliothek von der Startseite aus */
function showLibrary(): void {
  setView('end');
  const list = loadLibrary();
  const newest = list[list.length - 1];
  $('end').innerHTML = `<h2>Bibliothek</h2><p class="hint">Alle Reisen, die zu Ende gegangen sind; die neueste steht zuletzt im Regal. Ein Klick auf ein Buch zeigt die Reise.</p>
    <section class="end-lib"><div id="end-regal">${list.length ? regalHtml(list) : ''}</div>
    ${newest ? `<ol class="shelf book-detail" id="book-detail">${recordHtml(newest, undefined, list.length)}</ol>` : shelfHtml(list)}</section>
    <div class="actions"><button class="primary" id="end-title">Zur Startseite</button></div>`;
  bindRegal();
  $('end-title').onclick = () => showTitle();
  ($('end-title') as HTMLElement).focus({ preventScroll: true });
  scrollTop();
}

// ---------- Kampf im Run ----------

function openFight(): void {
  const run = app.run!;
  if (run.pending.kind !== 'fight') return;
  const { fight, party } = run.pending;
  const roster: Member[] = party.map((id) => {
    const f = friendById(run, id);
    return { key: f.id, species: f.species, friendClass: f.friendClass, name: f.name, profile: combatProfile(f, run), level: f.level, items: [...f.items], path: pathSteps(f), ...(f.wound ? { wounded: true } : {}), ward: f.ward ?? null, look: f.look ?? 0 };
  });
  const kindName = { kampf: 'Kampf', befallene: 'Befallene', hilferuf: 'Hilferuf', hinterhalt: 'Hinterhalt!', boss: 'Am Durchlass' }[fight.kind];
  const title = `${kindName} · ${biomeOfDay(run.day).name}, Tag ${run.day}, ${daylight(run).name}`;
  let chronIndex = run.chronicle.length;
  const xpBefore = new Map<string, number>();
  app.inShore = true;
  setView('shore');
  $('shore-title').textContent = title;
  openRunFight({
    title,
    roster,
    fieldSlots: fight.fieldSlots,
    enemies: fight.enemies,
    storm: fight.stage,
    retreat: app.retreat,
    noRetreat: fight.kind === 'boss',
    retreatOptions: retreatOptions(run),
    biome: biomeOfDay(run.day).id,
    boss: fight.kind === 'boss',
    // B25: Bande aus dem Gemeinschaftsraum, sichtbar beim Aufstellen
    ...(run.bonds?.length ? { bonds: { groups: run.bonds, reach: runRules.posts.bond.reach } } : {}),
    ...(fight.notes ? { notes: fight.notes } : {}),
    suggest: (ids) => autoPlace(run, ids),
    fight: (placements, retreat) => {
      if (fight.kind !== 'boss') app.retreat = retreat;
      chronIndex = run.chronicle.length;
      for (const id of party) xpBefore.set(id, friendById(run, id).xp);
      const setup = battleSetup(run, placements, retreat);
      const result = resolveFight(run, placements, retreat, { record: true });
      saveFight({
        runSeed: run.seed,
        chronLen: run.chronicle.length,
        title,
        roster,
        fieldSlots: fight.fieldSlots,
        enemies: fight.enemies,
        storm: fight.stage,
        retreat,
        setup,
        seed: fight.seed,
        notes: notes(),
        kept: keptIds(),
        biome: biomeOfDay(run.day).id,
        boss: fight.kind === 'boss',
      });
      save();
      return result;
    },
    afterNotes: () => notes(),
    onDone: fightDone,
    summaryShown: animateXp,
    kept: () => keptIds(),
    xpOf: (id) => xpShown(id),
  });

  function xpShown(id: string | null): number | undefined {
    const row = run.chronicle.slice(chronIndex).flatMap((e) => e.friends ?? []).find((r) => r.id === id);
    return row ? row.xp - (row.itemXp ?? 0) : undefined;
  }

  function keptIds(): string[] {
    return run.chronicle.slice(chronIndex).flatMap((e) => e.friends ?? []).filter((r) => r.kept).map((r) => r.id);
  }

  function notes(): string {
    // F15, F12, I18: was der Kampf dem Run gebracht hat; Spieltest Phase 4: die Beute in einem eigenen, deutlichen Bereich
    const entries = run.chronicle.slice(chronIndex);
    const rows = entries.flatMap((e) => e.friends ?? []);
    const loot = entries.find((e) => e.loot)?.loot;
    const box = loot
      ? `<div class="loot reveal r1"><h3>Beute</h3><div class="loot-row">
          <span class="loot-big">+${loot.wood} Holz</span>
          ${loot.items.map((i) => `<span class="loot-big item" data-tip="${esc(itemText(i))}">${esc(itemLabel(i))}</span>`).join('')}
          <span class="loot-small">Holz jetzt: ${run.wood}${loot.items.length ? ' · Items liegen im Inventar' : ''}</span>
        </div></div>`
      : '';
    // Spieltest Phase 4 (Fabi): Die XP laufen sichtbar in die Balken, wie bei Pokémon
    const bars = rows
      .filter((r) => !r.farewell)
      .map((r) => {
        const from = xpBefore.get(r.id) ?? 0;
        // Spieltest Phase 4 (Fabi): Was ein Item dazugibt, steht dabei, sonst passt die Zahl nicht zur Tabelle
        const bonus = r.itemXp ? ` <small>(+${r.itemXp} durch ${esc((r.itemXpFrom ?? []).join(' und ') || 'Items')})</small>` : '';
        return `<div class="xprow" data-from="${from}" data-to="${from + r.xp}"><b>${esc(r.name)}</b><span class="xplvl"></span><span class="xpmeter"><span></span></span><span class="xpgain">+${r.xp} XP${bonus}</span></div>`;
      })
      .join('');
    // Spieltest Phase 4 (Fabi): Die kleine Zeile „Kampf: gewonnen …“ fällt weg, das steht schon oben; Level-Ups leuchten in den Balken
    return `${box}${bars ? `<div class="xpbars reveal r2">${bars}</div>` : ''}`;
  }
}

/** Die XP-Balken nach dem Kampf füllen, mit Level-Up unterwegs (Spieltest Phase 4) */
function animateXp(root: HTMLElement): void {
  const rows = [...root.querySelectorAll<HTMLElement>('.xprow')];
  const max = runRules.xp.maxLevel;
  const paint = (row: HTMLElement, xp: number) => {
    const level = Math.min(max, levelForXp(xp));
    const into = xp - xpForLevel(level);
    const share = level >= max ? 1 : into / xpToNext(level);
    row.querySelector<HTMLElement>('.xplvl')!.textContent = `Lvl ${level}`;
    row.querySelector<HTMLElement>('.xpmeter span')!.style.width = `${Math.min(100, share * 100)}%`;
    return level;
  };
  // Die Balken laufen los, sobald sie eingeblendet sind (siehe .reveal.r2)
  const start = performance.now() + 750;
  const plan = rows.map((row) => {
    const from = Number(row.dataset.from);
    const to = Number(row.dataset.to);
    return { row, from, to, level: paint(row, from), ms: 900 + 500 * (levelForXp(to) - levelForXp(from)) };
  });
  const step = (now: number) => {
    let busy = false;
    for (const p of plan) {
      const t = Math.min(1, Math.max(0, (now - start) / p.ms));
      const eased = 1 - (1 - t) ** 2;
      const level = paint(p.row, p.from + (p.to - p.from) * eased);
      if (level > p.level) {
        p.level = level;
        p.row.classList.remove('up');
        void p.row.offsetWidth;
        p.row.classList.add('up');
      }
      if (t < 1) busy = true;
    }
    if (busy && document.body.contains(root)) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function fightDone(): void {
  saveFight(null);
  app.inShore = false;
  sync();
}

/** Nach dem Neuladen: den schon entschiedenen Kampf noch einmal abspielen, dann geht es normal weiter */
function replayFight(f: SavedFight): void {
  app.inShore = true;
  setView('shore');
  $('shore-title').textContent = f.title;
  const result = runBattle(f.setup, f.seed, { recordFrames: true });
  replayRunFight(
    {
      title: f.title,
      roster: f.roster,
      fieldSlots: f.fieldSlots,
      enemies: f.enemies,
      storm: f.storm,
      retreat: f.retreat,
      fight: () => result,
      afterNotes: () => f.notes,
      onDone: fightDone,
      summaryShown: animateXp,
      kept: () => f.kept ?? [],
      suggest: () => [],
      ...(f.biome ? { biome: f.biome } : {}),
      ...(f.boss ? { boss: true } : {}),
    },
    result,
  );
}

// ---------- Eingabe ----------

function viewRoot(): HTMLElement {
  if (document.body.classList.contains('dialog-open')) return $('dialog-card');
  return $(`view-${app.view}`);
}

function menuAction(a: Action, source: 'key' | 'pad'): void {
  const active = document.activeElement as HTMLElement | null;
  const root = viewRoot();
  if (app.view === 'river' && !document.body.classList.contains('dialog-open') && riverAction(runApp, a)) return;
  // B24: Unterwegs spult die Leertaste vor
  if (app.view === 'river' && !document.body.classList.contains('dialog-open') && traveling() && (a === 'confirm' || a === 'start')) {
    fastForward();
    return;
  }
  switch (a) {
    case 'confirm':
    case 'start':
      if (active && root.contains(active) && focusables(root).includes(active)) {
        if (active instanceof HTMLInputElement) return;
        active.click();
      } else focusFirst(root);
      return;
    case 'back':
      if (document.body.classList.contains('dialog-open')) {
        dialogBack(runApp);
        return;
      }
      if (app.view === 'chronicle') showRiver();
      return;
    case 'reroll':
      if (app.view === 'chronicle') showRiver();
      return;
    case 'slots':
      if (app.view === 'river') (document.querySelector('#crew .card') as HTMLElement | null)?.focus();
      return;
    default:
      void source;
  }
}

const input = createInput((a, source) => {
  // Fabi (Phase Kern): Esc öffnet überall das Menü; solange es offen ist, gehören ihm die Tasten
  // K46: Im Übungsufer löscht Esc erst den Befehl oder das Zielen, dann kommt das Menü
  if (a === 'menu' && !menuOpen() && app.view === 'practice' && practiceEsc()) return;
  if (a === 'menu') return toggleMenu();
  if (menuOpen()) {
    if (a === 'confirm' || a === 'start' || a === 'back') menuKey(a, document.activeElement as HTMLElement | null);
    return;
  }
  if (app.view === 'shore') return shoreAction(a, source);
  if (app.view === 'practice') return practiceAction(a);
  menuAction(a, source);
});
initMenu({
  hasRun: () => !!app.run && app.run.status === 'running',
  onTitle: () => app.view === 'title',
  newRun: () => startNewRun(),
  save: () => {
    if (!app.run) return false;
    save();
    return true;
  },
  get chronicle() {
    return app.run && app.run.status === 'running' && app.view === 'river' ? () => showChronicle() : null;
  },
  practice: () => {
    app.inShore = false;
    setView('shore');
    openPractice();
  },
  uebungsufer: () => showUebungsufer(),
  toTitle: () => showTitle(),
  redraw: () => shoreRedraw(),
});
initShore({ mouseUsed: input.mouseUsed });
// B24 (Erste Fassung): Kommt das Boot an, zeigt der Fluss die Entscheidung; ein Klick auf ein Tier an Bord öffnet seine Karte
initFahrt({
  arrived: () => {
    if (app.view === 'river' && app.run) showRiver();
  },
  openFriend: (id) => {
    if (!app.run || document.body.classList.contains('dialog-open')) return;
    openFriendCard(id);
    showRiver();
  },
});
bindFahrtCanvas($<HTMLCanvasElement>('fahrt-canvas'));
initPractice({ toTitle: () => showTitle() });
// Version 4 des Manifests: Die Flussleiste bekommt ihre Bilder, sobald sie geladen sind
window.addEventListener('art-loaded', () => {
  if (app.view === 'river' && app.run) renderRiver(runApp);
  // Runde 7: Die Startkachel kommt mit dem Manifest
  if (app.view === 'title' && !menuOpen()) showTitle();
});
window.addEventListener('pointermove', () => input.mouseUsed(), { passive: true });
initTips();
// Runde 5: Klänge des Sound-Leads; Knöpfe klopfen leise, Karten blättern um
initSound();
document.addEventListener(
  'click',
  (ev) => {
    const el = (ev.target as HTMLElement | null)?.closest?.('button, [data-flip]');
    if (!el || (el as HTMLButtonElement).disabled) return;
    play(el.hasAttribute('data-flip') ? 'umblaettern' : 'klick');
  },
  true,
);

$('shore-back').addEventListener('click', () => showTitle());
// F46 (Runde 7): Die Rindenkarte steht in ganzen Gerätepixeln je Bildpixel, so groß, wie es passt; U dreht sie um
applyCardZoom();
window.addEventListener('resize', () => applyCardZoom());
window.addEventListener('keydown', (ev) => {
  if (ev.key !== 'u' && ev.key !== 'U') return;
  if ((ev.target as HTMLElement | null)?.closest?.('input, textarea')) return;
  const card = document.querySelector<HTMLElement>('#dialog-card .rk');
  if (!card) return;
  card.classList.toggle('flipped');
  play('umblaettern');
});

const pulse = createPulse();
/** M9 (Runde 7): seit wann die Ankunft am Meer läuft */
let arrivalStart = 0;
loadEnde();
let last = performance.now();
function frame(now: number): void {
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  const st = input.poll();
  document.body.classList.toggle('pad', st.usingPad);
  if (menuOpen()) {
    // Das Menü hält alles an; Pfeile und Stick wählen darin
    const step = pulse(st.move.x, st.move.y, dt);
    if (step) menuMove(step[0], step[1]);
  } else if (app.view === 'shore') shoreFrame(dt, st);
  else if (app.view === 'practice') practiceFrame(dt);
  else {
    // Startbild des Übungsufers: der Tannenwald am Morgen mit dem Floß
    if (app.view === 'title') {
      // Runde 7: in Bildgröße malen, dann ganzzahlig vergrößert (nichts verzerrt)
      const scene = sceneCanvas('mood', 640, 360);
      if (drawMood(scene, now / 1000)) for (const mood of document.querySelectorAll<HTMLCanvasElement>('#view-title canvas.mood')) presentScene(mood, scene);
    }
    // B24: die Fahrt läuft
    if (app.view === 'river') fahrtFrame(dt);
    // M9 (Runde 7): Nach einem gewonnenen Run kommt das Boot am Meer an
    const arrival = app.view === 'end' && app.run ? (document.getElementById('end-arrival') as HTMLCanvasElement | null) : null;
    if (arrival && app.run) drawArrival(arrival, app.run, (now - arrivalStart) / 1000);
    const step = pulse(st.move.x, st.move.y, dt);
    if (step) moveFocus(viewRoot(), step[0], step[1]);
  }
  // F46: Bilder auf den Freundeskarten, sobald die Bilder geladen sind (auch am Ufer)
  paintKarten();
  // Runde 5: Klänge nach Lage, Biom und Sturmstufe
  const run = app.run;
  const mode = app.view === 'title' ? 'title' : app.view === 'shore' || app.view === 'practice' ? 'kampf' : app.view === 'end' ? 'ende' : run && run.status === 'running' ? 'fahrt' : 'still';
  setSoundScene({ mode, biome: run ? biomeOfDay(run.day).id : 'tannenwald', stage: run ? sim.stageNow(run) : 'ruhig', inside: insideAudible() });
  soundFrame(dt);
  requestAnimationFrame(frame);
}

// ---------- Start ----------

document.title = `${gameMeta.title} · Erste Fassung`;
const built = new Date(__BUILD_INFO__.builtAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'short', timeStyle: 'short' });
$('build').textContent = `Stand: ${__BUILD_INFO__.branch} · ${__BUILD_INFO__.commit} · gebaut ${built}`;
app.run = load();
const unfinished = app.run ? loadFight(app.run) : null;
if (app.run && unfinished) replayFight(unfinished);
else if (app.run && app.run.status === 'running') sync();
else {
  saveFight(null);
  showTitle();
}
requestAnimationFrame(frame);

if (__DEV__) {
  new EventSource('/esbuild').addEventListener('change', () => location.reload());
}

// Für die Konsole beim Ausprobieren
// sim und sync: zum Durchklicken ganzer Runs im Browser (Playwright), etwa fff.sim.land(fff.app.run, ids) und fff.sync()
(window as unknown as { fff: unknown }).fff = { app, getItem, getSpecies, displayName, fieldSlotsOf, shore: shoreDebug, practice: practiceDebug, sim, sync, fahrt: fahrtDebug, sound: soundDebug };
